import React, { Fragment } from "react"
import { TextMediaResolver } from "@/app/(site)/_templates/components/TextMediaTemplate"

import { IKTRichText } from "@/types/kontent.types"
import { TResolvedHighlightedContent } from "@/lib/kontent/components/getHighlightedContent"
import { TResolvedPub } from "@/lib/kontent/components/getPub"
import { TResolvedSimulateurChange } from "@/lib/kontent/components/getSimulateurChange"
import { TResolvedTextBlocks } from "@/lib/kontent/components/getTextBlocks"
import { TResolvedTextMedia } from "@/lib/kontent/components/getTextMedia"
import { TResolvedTextMediaGroup } from "@/lib/kontent/components/getTextMediaGroup"
import { TResolvedTextsList } from "@/lib/kontent/components/getTextsList"
import { cn, parseBody, TParsedBody } from "@/lib/utils"
import { TButton } from "@/components/ui/atoms/button/Button"
import { ButtonResolver } from "@/components/ui/atoms/button/Button.resolver"
import { HighlightedContentResolver } from "@/components/ui/atoms/highlighted-content/HighlightedContent.resolver"
import { AdNews } from "@/components/ui/molecules/ad/news/AdNews"
import {
  ChecklistSubscription,
  IChecklistSubscriptionProps,
} from "@/components/ui/molecules/checklist/subscription/ChecklistSubscription"
import { CurrencyConverter } from "@/components/ui/molecules/currency-converter/CurrencyConverter"
import { FeatureListResolver } from "@/components/ui/molecules/feature-list/FeatureList.resolver"
import { SimpleListResolver } from "@/components/ui/molecules/simple-list/SimpleList.resolver"
import { CardListResolver } from "@/components/ui/organisms/cards/card-list/CardList.resolver"
import { Picture } from "@/components/ui/molecules"
import WysiwygSimple from "@/components/WysiwygSimple"

interface IButtonGroupProps {
  buttons: React.JSX.Element[]
  hasGhostButton: boolean
  isPreviousButton: boolean
  className?: string
}

const ButtonGroup = ({
  buttons,
  hasGhostButton,
  isPreviousButton,
  className,
}: IButtonGroupProps) => {
  if (buttons.length === 0) return null
  return (
    <div
      className={cn(className, {
        "flex-button": true,
        "flex-button-column": hasGhostButton && isPreviousButton,
        "justify-center": className?.includes("text-center"),
      })}
    >
      {buttons}
    </div>
  )
}

/**
 * This rich text is used for elements which are 100% resolved before and don't need data to be fetched
 */
interface IRichTextSyncProps {
  body: IKTRichText
  linkPrefix?: string
  className?: string
  blockTitle?: string
  isInArticle?: boolean
  rightAlignButtons?: boolean
  preview?: boolean
}

const RichTextSync = React.forwardRef<HTMLDivElement, IRichTextSyncProps>(
  (
    {
      body,
      className,
      linkPrefix,
      isInArticle = false,
      rightAlignButtons = false,
      blockTitle,
      preview = false,
      ...props
    },
    ref
  ) => {
    const parsedBody: Array<TParsedBody> = parseBody(body, linkPrefix)

    type TBodyAcc = {
      nodes: React.ReactNode[]
      buttons: React.JSX.Element[]
      hasGhostButton: boolean
      isPreviousButton: boolean
    }

    const initialAcc: TBodyAcc = {
      nodes: [],
      buttons: [],
      hasGhostButton: false,
      isPreviousButton: false,
    }

    const { nodes, buttons, hasGhostButton, isPreviousButton } =
      parsedBody.reduce<TBodyAcc>((acc, el, i) => {
        try {
          if (el.type === "html" && ["", " ", "\n"].includes(el.content)) {
            return acc
          }

          const carryButtons = acc.isPreviousButton ? acc.buttons : []
          const isCurrentButton =
            el.type === "other" && el.element?.__typename === "Button"
          const carryHasGhostButton = isCurrentButton
            ? (el.element?.element as TButton | undefined)?.style === "Ghost"
            : acc.isPreviousButton
              ? acc.hasGhostButton
              : false

          const btnGroupProps = {
            buttons: carryButtons,
            hasGhostButton: carryHasGhostButton,
            isPreviousButton: isCurrentButton,
            className,
          }
          const isLast = i === parsedBody.length - 1

          switch (el.type) {
            case "other":
              if (el.element) {
                switch (el.element.__typename) {
                  case "Button": {
                    const buttonData = el.element.element as TButton
                    return {
                      nodes: acc.nodes,
                      buttons: [
                        ...carryButtons,
                        <ButtonResolver
                          key={buttonData.codename}
                          alignRight={rightAlignButtons}
                          analyticsTitle={blockTitle}
                          data={buttonData}
                        />,
                      ],
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  }
                  case "HighlightedContent":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <HighlightedContentResolver
                              variant={isInArticle ? "article" : "component"}
                              resolvedData={
                                el.element
                                  .element as TResolvedHighlightedContent
                              }
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "TextMediaGroup":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <CardListResolver
                              even={false}
                              variant="article"
                              resolvedData={
                                el.element.element as TResolvedTextMediaGroup
                              }
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "TextsList":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <SimpleListResolver
                              resolvedData={
                                el.element.element as TResolvedTextsList
                              }
                              isInArticle={isInArticle}
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "TextBlocks":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <FeatureListResolver
                              resolvedData={
                                el.element.element as TResolvedTextBlocks
                              }
                              isInArticle={isInArticle}
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "TextMedia":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div
                            className={cn(
                              "bg-primary-500 p-10",
                              isLast ? "" : "mb-7"
                            )}
                          >
                            <TextMediaResolver
                              data={el.element.element as TResolvedTextMedia}
                              isInArticle={isInArticle}
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "Pub":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <AdNews data={el.element.element as TResolvedPub} />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "SimulateurChange":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <CurrencyConverter
                              isReverse={
                                (
                                  el.element
                                    .element as TResolvedSimulateurChange
                                ).isReverse
                              }
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  case "ChecklistSubscription":
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                          <div className={cn(isLast ? "" : "mb-7")}>
                            <ChecklistSubscription
                              {...(el.element
                                .element as IChecklistSubscriptionProps)}
                              isInArticle={isInArticle}
                            />
                          </div>
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                  default:
                    return {
                      nodes: [
                        ...acc.nodes,
                        <div key={el.element._system_.codename}>
                          <ButtonGroup {...btnGroupProps} />
                        </div>,
                      ],
                      buttons: carryButtons,
                      hasGhostButton: carryHasGhostButton,
                      isPreviousButton: isCurrentButton,
                    }
                }
              }
              return {
                ...acc,
                buttons: carryButtons,
                hasGhostButton: carryHasGhostButton,
                isPreviousButton: isCurrentButton,
              }

            case "image":
              if (!el.element) {
                return {
                  ...acc,
                  buttons: carryButtons,
                  hasGhostButton: carryHasGhostButton,
                  isPreviousButton: isCurrentButton,
                }
              }
              return {
                nodes: [
                  ...acc.nodes,
                  <div key={`image-element-${el.element.imageId}`}>
                    <ButtonGroup {...btnGroupProps} />
                    <Picture
                      key={el.element.url}
                      src={el.element.url + "?h=3000&w=3000&fm=webp&q=80"}
                      alt={el.element.description}
                      variant={"default"}
                      width={el.element.width}
                      height={el.element.height}
                      className={cn(isLast ? "" : "mb-7")}
                    />
                  </div>,
                ],
                buttons: carryButtons,
                hasGhostButton: carryHasGhostButton,
                isPreviousButton: isCurrentButton,
              }
            case "html":
              return {
                nodes: [
                  ...acc.nodes,
                  <Fragment key={`wysiwygSimple-html-${el.content}`}>
                    <ButtonGroup {...btnGroupProps} />
                    <WysiwygSimple data={el.content} className="richtext" />
                  </Fragment>,
                ],
                buttons: carryButtons,
                hasGhostButton: carryHasGhostButton,
                isPreviousButton: isCurrentButton,
              }
            default:
              return {
                nodes: [
                  ...acc.nodes,
                  <div key={`unknown-block-${JSON.stringify(el)}`}>
                    <ButtonGroup {...btnGroupProps} />
                  </div>,
                ],
                buttons: carryButtons,
                hasGhostButton: carryHasGhostButton,
                isPreviousButton: isCurrentButton,
              }
          }
        } catch (e) {
          return {
            nodes: [
              ...acc.nodes,
              <div key={`error-${JSON.stringify(el)}`}>
                <ButtonGroup
                  buttons={acc.buttons}
                  hasGhostButton={acc.hasGhostButton}
                  isPreviousButton={acc.isPreviousButton}
                  className={className}
                />
              </div>,
            ],
            buttons: [],
            hasGhostButton: false,
            isPreviousButton: false,
          }
        }
      }, initialAcc)

    return (
      <div className={className} ref={ref} {...props}>
        {nodes}
        {buttons.length > 0 && isPreviousButton && (
          <ButtonGroup
            key="last"
            buttons={buttons}
            hasGhostButton={hasGhostButton}
            isPreviousButton={isPreviousButton}
            className={className}
          />
        )}
      </div>
    )
  }
)
RichTextSync.displayName = "RichTextSync"

export { RichTextSync, type IRichTextSyncProps }
